import { type ForwardedRef, forwardRef, useEffect } from 'react';

import { type ListCountriesQuery, type ListCountriesQueryVariables, useListCountriesQuery } from '@/common/graphql';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

type Variables = Partial<Omit<ListCountriesQueryVariables, 'pageNumber' | 'pageSize' | 'keyword' | 'hasNationalities'>>;
type Data = ComboboxItemWithFormatted & Omit<ListCountriesQuery['listCountries']['items'][number], 'id' | 'name'>;
type Props = CommonProps<Variables>;

const useQuery = (
  variables: Variables | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListCountriesQueryVariables, Data> => {
  const { data, ...rest } = useListCountriesQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      hasCompanies: variables?.hasCompanies ?? null,
      hasActiveCompanies: variables?.hasActiveCompanies ?? null,
      hasNationalities: null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  return {
    data:
      data?.listCountries?.items?.map(it => ({
        value: it.id,
        label: it.name,
        disabled: false,
        demonym: it.demonym,
      })) || [],
    ...rest,
  };
};

export const CountrySelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const CountrySelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(props.variables, props.onLoadingCompleted);
  return (
    <SelectMultiple multipleSelectedLabel={length => `${length} countries`} {...props} data={data} loading={loading} />
  );
};
